import type { SystemRoleKey } from "./schemas/role";
import type { TenantType } from "./schemas/tenant";

/**
 * Merkezî permission kataloğu (CLOS-83 `[026]`).
 *
 * Ürün genelindeki bütün yetkiler TEK yerde. Dağıtık tanımlanmış olsalardı "bu kullanıcı
 * neyi yapabiliyor?" sorusunun cevabı kod tabanını taramaktan geçerdi ve denetlenebilir
 * olmazdı.
 *
 * Katalog KOD, veri değil: aksiyonlar ürünün kendisiyle birlikte doğuyor ve derleme
 * zamanında bilinebiliyor. Veritabanına konsaydı, kodda var olmayan bir anahtar
 * atanabilir ya da kodda var olan bir anahtar katalogda hiç bulunmayabilirdi.
 * Rol→permission ATAMALARI ise tenant başına satır (DB); ikisi farklı şeyler.
 *
 * Biçim ve granülarite gerekçesi: `docs/adr/0021-permission-katalogu.md`.
 */

/** Katalog sürümü. Yeni anahtar eklendiğinde artar (kart: "versiyonlanabilir"). */
export const PERMISSION_CATALOG_VERSION = 1;

/**
 * Risk sınıfları (kart: "standart/hassas/korunan").
 *
 * Sınıf dekoratif değil, YÜK TAŞIYOR: varsayılan rol setini ve verme sınırını bu belirliyor.
 */
export const RISK_CLASSES = ["standard", "sensitive", "protected"] as const;
export type RiskClass = (typeof RISK_CLASSES)[number];

export const RISK_CLASS_NAMES: Record<RiskClass, string> = {
  standard: "Standart",
  sensitive: "Hassas",
  protected: "Korunan",
};

/** Permission domainleri — kartın "başlangıç permission grupları" listesi. */
export const PERMISSION_DOMAINS = [
  "contract",
  "template",
  "client",
  "organization",
  "ai",
  "subscription",
] as const;
export type PermissionDomain = (typeof PERMISSION_DOMAINS)[number];

export const PERMISSION_DOMAIN_NAMES: Record<PermissionDomain, string> = {
  contract: "Sözleşmeler",
  template: "Şablonlar",
  client: "Müvekkiller ve hizmet ilişkileri",
  organization: "Organizasyon yönetimi",
  ai: "AI",
  subscription: "Abonelik",
};

export type PermissionEntry = {
  /** Kanonik anahtar: `<domain>.<action>`. Benzersiz ve DEĞİŞMEZ. */
  key: string;
  domain: PermissionDomain;
  risk: RiskClass;
  /** Kullanıcıya gösterilen açıklama (kart: "kullanıcıya gösterilen açıklamalar"). */
  description: string;
  /**
   * Önkoşul permission'lar. Eksikse atama reddedilir ve kullanıcıya açıklanır.
   *
   * Kural UYDURULMADI, yapısal: bir domaindeki her aksiyon o domainin TEMEL aksiyonuna
   * bağlı — göremediğin şeyi düzenleyemezsin. AI zincirinde ek bir bağ var: çıktıyı
   * uygulamak veya kaydetmek, önce onu görebilmeyi gerektirir.
   */
  requires: readonly string[];
};

/**
 * Domain içi aksiyonlar. `view` her domainin temeli ve bağımlılık kökü.
 *
 * Aksiyon listeleri kartın "başlangıç permission grupları" bölümünden BİREBİR alındı;
 * granülarite aksiyon ailesi düzeyinde (ADR-0021). Daha ince bölmek (alan bazlı yetki)
 * kartsız bir ürün davranışı uydurmak olurdu.
 */
type Aksiyon = { action: string; risk: RiskClass; description: string };

const CONTRACT_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "Sözleşmeleri görüntüleme" },
  { action: "create", risk: "standard", description: "Sözleşme oluşturma" },
  { action: "edit", risk: "standard", description: "Sözleşme düzenleme" },
  { action: "file", risk: "standard", description: "Sözleşme dosyalarını yönetme" },
  { action: "comment", risk: "standard", description: "Sözleşmeye yorum yazma" },
  { action: "revision", risk: "standard", description: "Sözleşme revizyonu oluşturma" },
  { action: "history", risk: "standard", description: "Sözleşme geçmişini görüntüleme" },
  { action: "review", risk: "standard", description: "Sözleşmeyi inceleme" },
  { action: "share", risk: "sensitive", description: "Sözleşmeyi paylaşma" },
  { action: "approve", risk: "sensitive", description: "Sözleşmeyi onaylama" },
  { action: "reconcile", risk: "sensitive", description: "Sözleşme mutabakatını yürütme" },
  { action: "sign", risk: "sensitive", description: "Sözleşmeyi imzalama" },
  { action: "archive", risk: "sensitive", description: "Sözleşmeyi arşivleme" },
];

const TEMPLATE_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "Şablonları görüntüleme" },
  { action: "create", risk: "standard", description: "Şablon oluşturma" },
  { action: "edit", risk: "standard", description: "Şablon düzenleme" },
  { action: "use", risk: "standard", description: "Şablonu kullanma" },
  { action: "archive", risk: "sensitive", description: "Şablonu arşivleme" },
  { action: "manage", risk: "sensitive", description: "Şablon kütüphanesini yönetme" },
];

const CLIENT_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "Müvekkilleri görüntüleme" },
  { action: "create", risk: "standard", description: "Müvekkil oluşturma" },
  { action: "edit", risk: "standard", description: "Müvekkil bilgilerini düzenleme" },
  { action: "representative", risk: "sensitive", description: "Müvekkil temsilcisi yönetme" },
  { action: "portfolio", risk: "sensitive", description: "Müvekkil portföyünü yönetme" },
  { action: "service_scope", risk: "sensitive", description: "Hizmet kapsamını yönetme" },
];

const ORGANIZATION_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "Organizasyon bilgilerini görüntüleme" },
  { action: "profile", risk: "sensitive", description: "Organizasyon profilini düzenleme" },
  { action: "member", risk: "sensitive", description: "Kullanıcı ve üyelikleri yönetme" },
  { action: "invite", risk: "sensitive", description: "Organizasyona kullanıcı davet etme" },
  { action: "department", risk: "sensitive", description: "Departmanları yönetme" },
  { action: "role", risk: "protected", description: "Rolleri yönetme" },
  { action: "permission", risk: "protected", description: "Rol yetkilerini yönetme" },
];

const AI_ACTIONS: Aksiyon[] = [
  { action: "use", risk: "standard", description: "AI işlevini kullanma" },
  { action: "output", risk: "standard", description: "AI çıktısını görüntüleme" },
  { action: "apply", risk: "standard", description: "AI çıktısını uygulama" },
  { action: "persist", risk: "sensitive", description: "AI çıktısını kalıcı kaydetme" },
];

const SUBSCRIPTION_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "protected", description: "Abonelik bilgilerini görüntüleme" },
  { action: "plan", risk: "protected", description: "Abonelik planını yönetme" },
  { action: "usage", risk: "protected", description: "Abonelik kullanımını görüntüleme" },
  { action: "entitlement", risk: "protected", description: "Ek hakları yönetme" },
  { action: "payment", risk: "protected", description: "Ödeme işlemlerini yürütme" },
  { action: "invoice", risk: "protected", description: "Faturalama işlemlerini yürütme" },
];

/**
 * AI zincirindeki ek yapısal bağ.
 *
 * Temel aksiyon kuralı AI'da yetmiyor: çıktıyı UYGULAMAK ya da KAYDETMEK, önce onu
 * görebilmeyi gerektiriyor. Görmediği bir çıktıyı uygulayan kullanıcı, ne uyguladığını
 * bilmiyor demektir.
 */
const AI_EXTRA_REQUIREMENTS: Record<string, readonly string[]> = {
  "ai.apply": ["ai.output"],
  "ai.persist": ["ai.output"],
};

/**
 * `kokAksiyon`, domainin TEMEL aksiyonudur — diğer her aksiyon ona bağlanır.
 *
 * Sabit `view` varsayamıyoruz: AI domaininde görüntüleme diye bir aksiyon yok, temel
 * aksiyon `use`. İlk yazımda `view` sabitlenmişti ve katalog var olmayan bir `ai.view`
 * önkoşulunu üretiyordu — varsayılan setlerin bağımlılık tutarlılığını ölçen test yakaladı.
 */
function domainKatalogu(
  domain: PermissionDomain,
  aksiyonlar: Aksiyon[],
  kokAksiyon: string,
): PermissionEntry[] {
  const kok = `${domain}.${kokAksiyon}`;
  return aksiyonlar.map((a) => {
    const key = `${domain}.${a.action}`;
    const ekstra = AI_EXTRA_REQUIREMENTS[key] ?? [];
    const temel = key === kok ? [] : [kok];
    return {
      key,
      domain,
      risk: a.risk,
      description: a.description,
      requires: [...new Set([...temel, ...ekstra])].filter((k) => k !== key),
    };
  });
}

export const PERMISSION_CATALOG: readonly PermissionEntry[] = [
  ...domainKatalogu("contract", CONTRACT_ACTIONS, "view"),
  ...domainKatalogu("template", TEMPLATE_ACTIONS, "view"),
  ...domainKatalogu("client", CLIENT_ACTIONS, "view"),
  ...domainKatalogu("organization", ORGANIZATION_ACTIONS, "view"),
  ...domainKatalogu("ai", AI_ACTIONS, "use"),
  ...domainKatalogu("subscription", SUBSCRIPTION_ACTIONS, "view"),
];

const KATALOG_INDEKS = new Map(PERMISSION_CATALOG.map((p) => [p.key, p]));

export const PERMISSION_KEYS: readonly string[] = PERMISSION_CATALOG.map((p) => p.key);

export function findPermission(key: string): PermissionEntry | undefined {
  return KATALOG_INDEKS.get(key);
}

export function isKnownPermission(key: string): boolean {
  return KATALOG_INDEKS.has(key);
}

/**
 * KORUNAN permission özel role atanamaz (kart kriteri).
 *
 * Verme sınırı burada tanımlanır, uygulama noktalarında değil: her yeni atama akışı kendi
 * kontrolünü yazsaydı biri unutulur ve o yoldan yetki yükseltme mümkün olurdu.
 */
export function isGrantableToCustomRole(key: string): boolean {
  return findPermission(key)?.risk !== "protected";
}

/**
 * Bir permission kümesindeki EKSİK önkoşullar.
 *
 * Boş dizi dönerse küme tutarlı. Dönen her anahtar, kullanıcıya "şunu da seçmelisiniz"
 * diye gösterilecek somut bir eksiktir — kart: *"eksik önkoşullar reddedilir ve kullanıcıya
 * açıklanır."*
 */
export function missingPrerequisites(keys: readonly string[]): string[] {
  const secilen = new Set(keys);
  const eksik = new Set<string>();
  for (const key of keys) {
    for (const onkosul of findPermission(key)?.requires ?? []) {
      if (!secilen.has(onkosul)) eksik.add(onkosul);
    }
  }
  return [...eksik].sort();
}

/**
 * Bir kümeyi BAĞIMLILIK OLARAK TUTARLI hâle budar (CLOS-87 `[030]`).
 *
 * Çoğaltmada kaynak rolün bazı yetkileri düşebiliyor (katalogdan çıkmış, korunan sınıfa
 * geçmiş ya da aktörde yok). Önkoşulu düşen bir yetkiyi öylece bırakmak, kaydedilemeyen
 * bir küme üretirdi — kullanıcıya *"kopyaladım ama kaydedemiyorum"* demek zorunda kalırdık.
 *
 * Döngü ŞART: bir yetkiyi çıkarmak, ona bağlı başka bir yetkiyi de geçersiz kılabilir.
 * Tek geçişte budamak zinciri yarım bırakırdı.
 */
export function pruneToConsistent(keys: readonly string[]): string[] {
  let kalan = [...new Set(keys)].filter(isKnownPermission);

  for (;;) {
    const kume = new Set(kalan);
    const sonraki = kalan.filter((key) =>
      (findPermission(key)?.requires ?? []).every((onkosul) => kume.has(onkosul)),
    );
    if (sonraki.length === kalan.length) return sonraki.sort();
    kalan = sonraki;
  }
}

/**
 * Çalışma alanı türünde KULLANILABİLİR permission'lar.
 *
 * Müvekkil ve hizmet ilişkisi domaini yalnız HUKUK BÜROSUNDA var: müvekkil hizmet
 * ilişkileri hukuk bürosunun bağlamıdır (CLOS-72 `[024]`). Şirkette de açık olsaydı,
 * hiçbir zaman doldurulmayacak bir yüzeye yetki verilmiş olurdu.
 *
 * Kişisel çalışma alanı sistem rolü üretmiyor (CLOS-82 `[025]`), dolayısıyla varsayılan
 * atama da almıyor.
 */
export function permissionsForTenantType(type: TenantType): PermissionEntry[] {
  if (type === "enterprise_law_firm") return [...PERMISSION_CATALOG];
  if (type === "enterprise_company") {
    return PERMISSION_CATALOG.filter((p) => p.domain !== "client");
  }
  return [];
}

/**
 * Sistem rolünün VARSAYILAN permission seti.
 *
 * Kural risk sınıfından TÜRETİLİYOR, elle yazılmış bir tablodan değil:
 *
 * - **Organizasyon Yöneticisi** — türde kullanılabilir her permission. Korunan sınıfın
 *   *"yalnız kanonik yetkili roller"* sahibi bu rol (kart: abonelik korunan sınıftadır).
 * - **Standart Kullanıcı** — yalnız `standard` sınıfı. Hassas işlemler (imza, onay,
 *   paylaşım, arşiv, üyelik) geri alınamaz ya da organizasyon dışına çıkan sonuçlar
 *   doğuruyor; varsayılan olarak herkese vermek, yetkiyi varsayılan olarak açmak demekti.
 *
 * Elle tablo yazılsaydı yeni bir permission eklendiğinde iki listeyi de güncellemek
 * gerekir ve biri unutulurdu — unutulan taraf çoğu zaman kısıtlayıcı olan olur, yani
 * yetki sessizce genişlerdi.
 */
export function defaultPermissionsFor(role: SystemRoleKey, type: TenantType): string[] {
  const kullanilabilir = permissionsForTenantType(type);
  if (role === "org_admin") return kullanilabilir.map((p) => p.key);
  return kullanilabilir.filter((p) => p.risk === "standard").map((p) => p.key);
}
